import { useState, useEffect } from "react";
import {
  Receipt,
  Search,
  Filter,
  Calendar,
  User,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit,
  Eye,
  Download,
  CreditCard,
  Printer,
} from "lucide-react";
import medicalDB from "../services/database";
import TransactionForm from "./TransactionForm";

const TransactionList = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchTerm, statusFilter, dateFilter, paymentMethodFilter]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const allTransactions = await medicalDB.getAllTransactions();
      setTransactions(allTransactions);
    } catch (error) {
      console.error("Error loading transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (transaction) =>
          transaction.patient?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.receiptNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          transaction.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.status === statusFilter
      );
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.date);
        const transactionDay = new Date(
          transactionDate.getFullYear(),
          transactionDate.getMonth(),
          transactionDate.getDate()
        );

        switch (dateFilter) {
          case "today":
            return transactionDay.getTime() === today.getTime();
          case "week":
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return transactionDay >= weekAgo;
          case "month":
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return transactionDay >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Payment method filter
    if (paymentMethodFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.paymentMethod === paymentMethodFilter
      );
    }

    setFilteredTransactions(filtered);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case medicalDB.PAYMENT_STATUS?.PAID || "paid":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case medicalDB.PAYMENT_STATUS?.PARTIAL || "partial":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case medicalDB.PAYMENT_STATUS?.PENDING || "pending":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case medicalDB.PAYMENT_STATUS?.OVERDUE || "overdue":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case medicalDB.PAYMENT_STATUS?.PAID || "paid":
        return "text-green-600 bg-green-50 border-green-200";
      case medicalDB.PAYMENT_STATUS?.PARTIAL || "partial":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case medicalDB.PAYMENT_STATUS?.PENDING || "pending":
        return "text-red-600 bg-red-50 border-red-200";
      case medicalDB.PAYMENT_STATUS?.OVERDUE || "overdue":
        return "text-red-700 bg-red-100 border-red-300";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const formatPaymentMethod = (method) => {
    switch (method) {
      case medicalDB.PAYMENT_METHODS?.CASH || "cash":
        return "Cash";
      case medicalDB.PAYMENT_METHODS?.CARD || "card":
        return "Card";
      case medicalDB.PAYMENT_METHODS?.CHECK || "check":
        return "Check";
      case medicalDB.PAYMENT_METHODS?.BANK_TRANSFER || "bank_transfer":
        return "Bank Transfer";
      default:
        return method;
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setShowTransactionForm(true);
  };

  const handleTransactionSaved = () => {
    setShowTransactionForm(false);
    setEditingTransaction(null);
    loadTransactions();
  };

  const exportTransactions = () => {
    const csvContent = [
      [
        "Receipt Number",
        "Date",
        "Patient",
        "Total Amount (₪)",
        "Paid Amount (₪)",
        "Outstanding (₪)",
        "Payment Method",
        "Status",
        "Visit Type",
        "Services",
        "Notes",
      ],
      ...filteredTransactions.map((t) => [
        t.receiptNumber,
        new Date(t.date).toLocaleDateString("en-IL"),
        t.patient?.name || "Unknown",
        t.totalAmount.toFixed(2),
        t.paidAmount.toFixed(2),
        (t.totalAmount - t.paidAmount).toFixed(2),
        formatPaymentMethod(t.paymentMethod),
        t.status,
        t.items?.find((item) => item.type === "visit")?.name || "N/A",
        t.items?.map((item) => `${item.name} (₪${item.price})`).join("; ") ||
          "N/A",
        t.notes || "",
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printTransactions = () => {
    const printWindow = window.open("", "_blank");
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    // Calculate totals
    const totalAmount = filteredTransactions.reduce(
      (sum, t) => sum + t.totalAmount,
      0
    );
    const totalPaid = filteredTransactions.reduce(
      (sum, t) => sum + t.paidAmount,
      0
    );
    const totalOutstanding = totalAmount - totalPaid;

    const reportHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Transactions Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              font-size: 12px;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .clinic-name {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .report-title {
              font-size: 16px;
              font-weight: bold;
              margin: 10px 0;
            }
            .report-info {
              display: flex;
              justify-content: space-between;
              margin: 10px 0;
              font-size: 11px;
            }
            .summary {
              background-color: #f5f5f5;
              padding: 15px;
              margin: 20px 0;
              border: 1px solid #ddd;
            }
            .summary-title {
              font-weight: bold;
              font-size: 14px;
              margin-bottom: 10px;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 15px;
            }
            .summary-item {
              text-align: center;
            }
            .summary-label {
              font-size: 10px;
              color: #666;
              margin-bottom: 5px;
            }
            .summary-value {
              font-size: 14px;
              font-weight: bold;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .status-paid { color: #059669; }
            .status-pending { color: #d97706; }
            .status-partial { color: #dc2626; }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="clinic-name">Medical Clinic</div>
            <div class="report-title">TRANSACTIONS REPORT</div>
          </div>
          
          <div class="report-info">
            <div>Generated: ${currentDate} ${currentTime}</div>
            <div>Total Records: ${filteredTransactions.length}</div>
          </div>
          
          <div class="summary">
            <div class="summary-title">Financial Summary</div>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Total Revenue</div>
                <div class="summary-value">₪${totalAmount.toFixed(2)}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Collected</div>
                <div class="summary-value">₪${totalPaid.toFixed(2)}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Outstanding</div>
                <div class="summary-value">₪${totalOutstanding.toFixed(2)}</div>
              </div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Date</th>
                <th>Patient</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Method</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${filteredTransactions
                .map(
                  (transaction) => `
                <tr>
                  <td>${transaction.receiptNumber}</td>
                  <td>${new Date(transaction.date).toLocaleDateString()}</td>
                  <td>${transaction.patient?.name || "Unknown"}</td>
                  <td>₪${transaction.totalAmount.toFixed(2)}</td>
                  <td>₪${transaction.paidAmount.toFixed(2)}</td>
                  <td>${formatPaymentMethod(transaction.paymentMethod)}</td>
                  <td class="status-${transaction.status}">${
                    transaction.status
                  }</td>
                  <td>${transaction.notes || "-"}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="footer">
            <div>This report contains ${
              filteredTransactions.length
            } transactions</div>
            <div>Generated on ${currentDate} at ${currentTime}</div>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(reportHtml);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={printTransactions}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Printer className="h-4 w-4" />
            Print Report
          </button>
          <button
            onClick={exportTransactions}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Patient name, receipt number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="check">Check</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>
            Showing {filteredTransactions.length} of {transactions.length}{" "}
            transactions
          </span>
          <span>
            Total: ₪
            {filteredTransactions
              .reduce((sum, t) => sum + t.totalAmount, 0)
              .toFixed(2)}
          </span>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredTransactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Receipt className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {transaction.receiptNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {transaction.patient?.name || "Unknown Patient"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {new Date(transaction.date).toLocaleDateString(
                            "en-IL"
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          ₪{transaction.totalAmount.toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Paid: ₪{transaction.paidAmount.toFixed(2)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {formatPaymentMethod(transaction.paymentMethod)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          transaction.status
                        )}`}
                      >
                        {getStatusIcon(transaction.status)}
                        {transaction.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditTransaction(transaction)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="Edit transaction"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Receipt className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No transactions found
            </h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== "all" || dateFilter !== "all"
                ? "Try adjusting your filters to see more results."
                : "Transactions will appear here when payments are recorded."}
            </p>
          </div>
        )}
      </div>

      {/* Transaction Form Modal */}
      <TransactionForm
        isOpen={showTransactionForm}
        onClose={() => {
          setShowTransactionForm(false);
          setEditingTransaction(null);
        }}
        onSave={handleTransactionSaved}
        patientId={editingTransaction?.patientId}
        initialData={editingTransaction}
      />
    </div>
  );
};

export default TransactionList;
